import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

export const SITE_FIELDS = [
  { key: "hero.line1", label: "Homepage headline — line 1", def: "Showroom" },
  { key: "hero.line2", label: "Homepage headline — line 2 (highlighted)", def: "Detailing" },
  { key: "hero.rating", label: "Rating line", def: "5.0 Rated • Maricopa & surrounding cities" },
  { key: "hero.description", label: "Homepage description", def: "Premium mobile auto detailing. We bring the showroom finish directly to your doorstep with professional-grade products.", long: true },
  { key: "hero.cta_book", label: "Book button text", def: "Book Now" },
  { key: "hero.arc_text", label: "Curved Book Now text above the car", def: "Book Now", hidden: true },
  { key: "wash.car", label: "Wash animation car", def: "rangerover", hidden: true },
  { key: "wash.style", label: "Wash animation style", def: "wipe", hidden: true },
  { key: "wash.effect", label: "Wash animation effect", def: "bubbles", hidden: true },
  { key: "wash.ending", label: "Wash animation ending", def: "clean", hidden: true },
  ...["Scroll to wash it.", "Look closer.", "Swirls out.", "Glass on.", "Jaw drops.", "Keys back."].map((def, i) => ({ key: `wash.s${i + 1}`, label: `Homepage wash animation — scene ${i + 1} text`, def })),
  { key: "services.title", label: "Services heading (*word* = highlighted)", def: "Premium *Detailing* Packages" },
  { key: "ceramic.title", label: "Ceramic heading", def: "Professional *Ceramic* Coating" },
  { key: "portfolio.title", label: "Portfolio heading", def: "Real *Results*" },
  { key: "testimonials.title", label: "Reviews heading", def: "What Our *Clients* Say" },
  { key: "contact.title", label: "Contact heading", def: "Get a Free *Quote*" },
];

const SiteCtx = createContext({ content: {}, reviewLink: "", loaded: false });

export const SiteContentProvider = ({ children }) => {
  const [state, setState] = useState({ content: {}, reviewLink: "", loaded: false });
  useEffect(() => {
    const look = new URLSearchParams(window.location.search).get("preview_look");
    const lookReq = look ? api.get(`/admin/hero/looks/${look}`).then((r) => r.data.content).catch(() => null) : Promise.resolve(null);
    Promise.all([api.get("/site/content"), lookReq]).then(([r, lc]) => {
      const base = r.data.content || {};
      const content = lc ? { ...Object.fromEntries(Object.entries(base).filter(([k]) => !k.startsWith("hero.") || k.startsWith("hero.anchor_"))), ...lc } : base;
      setState({ content, reviewLink: r.data.google_review_link || "", loaded: true });
    }).catch(() => setState((x) => ({ ...x, loaded: true })));
  }, []);
  return <SiteCtx.Provider value={state}>{children}</SiteCtx.Provider>;
};

export const useSite = (key) => {
  const { content } = useContext(SiteCtx);
  const f = SITE_FIELDS.find((x) => x.key === key);
  return content[key] || f?.def || "";
};

export const useSiteLoaded = () => useContext(SiteCtx).loaded;

// Lets the admin preview render site components with unsaved edits
export const SiteContentOverride = ({ content, children }) => <SiteCtx.Provider value={{ content, reviewLink: "", loaded: true }}>{children}</SiteCtx.Provider>;

export const useReviewLink = () => useContext(SiteCtx).reviewLink;

export const Rich = ({ text }) =>
  (text || "").split(/(\*[^*]+\*)/).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") ? <span key={i} className="gradient-text">{part.slice(1, -1)}</span> : <span key={i}>{part}</span>
  );

export const SiteHeading = ({ k }) => <Rich text={useSite(k)} />;

export const useSiteText = () => {
  const { content } = useContext(SiteCtx);
  return (key) => content[key] || SITE_FIELDS.find((x) => x.key === key)?.def || "";
};
